/**
 * uPlot plugin: trade entry (filled triangle) and exit (hollow triangle) markers.
 * Direction per trade type: up for long entry / short exit, down for short entry / long exit.
 * Uses the same price-area mapping as the OHLC chart (volume strip excluded).
 */
import type uPlot from 'uplot';
import type { SimpleBacktestTrade } from '../state/types';

const ENTRY_FILL = '#1a1a1a';
const EXIT_STROKE = '#1a1a1a';
const SELECTED_ENTRY_FILL = '#1a1a1a';
const SELECTED_EXIT_STROKE = '#1a1a1a';
const SELECTED_MARKER_SCALE = 1.35;
const BAND_FILL = 'rgba(0, 0, 0, 0.08)';
const HOVER_ENTRY_FILL = '#2563eb';
const HOVER_EXIT_STROKE = '#2563eb';
const ANNOTATION_FONT = '18px sans-serif';
const ANNOTATION_BG = 'rgba(255, 255, 255, 0.92)';
const ANNOTATION_FG = '#1a1a1a';
const ANNOTATION_PADDING = 12;

export type TradeMarkersPluginOpts = {
  /** Returns the list of trades to draw (e.g. selected set from store). */
  getTrades: () => SimpleBacktestTrade[];
  /** Returns index of selected trade in that list, or null. Selected trade gets band + emphasized markers. */
  getSelectedTradeIndex: () => number | null;
  /** Maps backend bar index (entry_bar/exit_bar from API) to chart x-axis index. Aligns by date so markers match table. */
  getChartIndexForBackendBar: (backendBarIndex: number) => number;
  /** Fraction of chart height used for volume strip (e.g. 0.2). Must match primary chart. */
  volumeStripRatio: number;
  /** If provided, use for marker y-position and hover label (chart display price set). Otherwise use trade.entry_price/exit_price. */
  getEntryPriceForMarker?: (trade: SimpleBacktestTrade) => number;
  getExitPriceForMarker?: (trade: SimpleBacktestTrade) => number;
};

/**
 * Draw a triangle at (cx, cy). pointUp: true = tip at top (base at bottom), false = tip at bottom.
 * halfW and halfH are half-width and half-height in px.
 */
function drawTriangle(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  halfW: number,
  halfH: number,
  pointUp: boolean,
  filled: boolean,
  strokeStyle: string
) {
  ctx.save();
  ctx.strokeStyle = strokeStyle;
  if (filled) ctx.fillStyle = strokeStyle;

  ctx.beginPath();
  if (pointUp) {
    ctx.moveTo(cx, cy - halfH);
    ctx.lineTo(cx - halfW, cy + halfH);
    ctx.lineTo(cx + halfW, cy + halfH);
  } else {
    ctx.moveTo(cx, cy + halfH);
    ctx.lineTo(cx - halfW, cy - halfH);
    ctx.lineTo(cx + halfW, cy - halfH);
  }
  ctx.closePath();
  if (filled) ctx.fill();
  ctx.stroke();
  ctx.restore();
}

export function tradeMarkersPlugin(opts: TradeMarkersPluginOpts): uPlot.Plugin {
  const {
    getTrades,
    getSelectedTradeIndex,
    getChartIndexForBackendBar,
    volumeStripRatio,
    getEntryPriceForMarker,
    getExitPriceForMarker,
  } = opts;

  return {
    hooks: {
      draw: (u: uPlot) => {
        const trades = getTrades();
        const selectedIdx = getSelectedTradeIndex();

        u.ctx.save();

        const { top, height, width } = u.bbox;
        const priceHeight = height * (1 - volumeStripRatio);
        const toPriceY = (fullBboxY: number) =>
          top + ((fullBboxY - top) / height) * priceHeight;

        const idxs = u.series[0].idxs;
        if (!idxs) {
          u.ctx.restore();
          return;
        }
        const iMin = idxs[0];
        const iMax = idxs[1];
        const visibleBarCount = Math.max(1, iMax - iMin + 1);
        const pixelsPerBar = width / visibleBarCount;
        const halfSize = Math.max(4, Math.min(8, pixelsPerBar * 0.45));

        let cursorBarIdx: number | null = null;
        if (
          u.cursor?.left != null &&
          Number.isFinite(u.cursor.left) &&
          u.cursor.left >= 0
        ) {
          const scaleVal = u.posToVal(u.cursor.left, 'x', false);
          const idx = u.valToIdx(scaleVal);
          if (idx != null && Number.isFinite(idx)) {
            const n = (u.data[0] as unknown[]).length;
            const rounded = Math.round(idx);
            if (rounded >= 0 && rounded < n) cursorBarIdx = rounded;
          }
        }

        let hoveredTradeIdx: number | null = null;
        if (cursorBarIdx != null && trades.length > 0) {
          for (let i = 0; i < trades.length; i++) {
            const t = trades[i];
            const lo = Math.min(
              getChartIndexForBackendBar(t.entry_bar),
              getChartIndexForBackendBar(t.exit_bar)
            );
            const hi = Math.max(
              getChartIndexForBackendBar(t.entry_bar),
              getChartIndexForBackendBar(t.exit_bar)
            );
            if (cursorBarIdx >= lo && cursorBarIdx <= hi) {
              hoveredTradeIdx = i;
              break;
            }
          }
        }

        const selectedTrade =
          selectedIdx != null && selectedIdx >= 0 && selectedIdx < trades.length
            ? trades[selectedIdx]
            : null;

        if (selectedTrade) {
          const { entry_bar, exit_bar } = selectedTrade;
          const chartMin = Math.min(
            getChartIndexForBackendBar(entry_bar),
            getChartIndexForBackendBar(exit_bar)
          );
          const chartMax = Math.max(
            getChartIndexForBackendBar(entry_bar),
            getChartIndexForBackendBar(exit_bar)
          );
          const xValMin = u.scales.x.distr === 2 ? chartMin : u.data[0][chartMin];
          const xValMax = u.scales.x.distr === 2 ? chartMax : u.data[0][chartMax];
          const xLeft = u.valToPos(xValMin, 'x', true);
          const xRight = u.valToPos(xValMax, 'x', true);
          const bandWidth = Math.max(1, xRight - xLeft);
          u.ctx.fillStyle = BAND_FILL;
          u.ctx.fillRect(xLeft, top, bandWidth, priceHeight);
        }

        if (trades.length === 0) {
          u.ctx.restore();
          return;
        }

        for (let i = 0; i < trades.length; i++) {
          const trade = trades[i];
          const { trade_direction, entry_bar, exit_bar } = trade;
          const entryPrice = getEntryPriceForMarker ? getEntryPriceForMarker(trade) : trade.entry_price;
          const exitPrice = getExitPriceForMarker ? getExitPriceForMarker(trade) : trade.exit_price;
          const entryChartIdx = getChartIndexForBackendBar(entry_bar);
          const exitChartIdx = getChartIndexForBackendBar(exit_bar);
          const isSelected = i === selectedIdx;
          const isHovered = i === hoveredTradeIdx;
          const size = isSelected ? halfSize * SELECTED_MARKER_SCALE : halfSize;
          const entryColor = isHovered
            ? HOVER_ENTRY_FILL
            : isSelected
              ? SELECTED_ENTRY_FILL
              : ENTRY_FILL;
          const exitColor = isHovered
            ? HOVER_EXIT_STROKE
            : isSelected
              ? SELECTED_EXIT_STROKE
              : EXIT_STROKE;
          const lineWidth = isSelected ? 2 : 1;
          if (isSelected) u.ctx.lineWidth = lineWidth;

          if (entryChartIdx >= iMin && entryChartIdx <= iMax) {
            const xVal = u.scales.x.distr === 2 ? entryChartIdx : u.data[0][entryChartIdx];
            const x = u.valToPos(xVal, 'x', true);
            const fullY = u.valToPos(entryPrice, 'y', true);
            const y = toPriceY(fullY);
            const pointUp = trade_direction === 'long';
            drawTriangle(u.ctx, x, y, size, size, pointUp, true, entryColor);
          }

          if (exitChartIdx >= iMin && exitChartIdx <= iMax) {
            const xVal = u.scales.x.distr === 2 ? exitChartIdx : u.data[0][exitChartIdx];
            const x = u.valToPos(xVal, 'x', true);
            const fullY = u.valToPos(exitPrice, 'y', true);
            const y = toPriceY(fullY);
            const pointUp = trade_direction === 'short';
            drawTriangle(u.ctx, x, y, size, size, pointUp, false, exitColor);
          }

          if (isSelected) u.ctx.lineWidth = 1;
        }

        if (hoveredTradeIdx != null && hoveredTradeIdx >= 0 && hoveredTradeIdx < trades.length) {
          const hovered = trades[hoveredTradeIdx];
          const entryPrice = getEntryPriceForMarker ? getEntryPriceForMarker(hovered) : hovered.entry_price;
          const exitPrice = getExitPriceForMarker ? getExitPriceForMarker(hovered) : hovered.exit_price;
          const entryChartIdx = getChartIndexForBackendBar(hovered.entry_bar);
          const exitChartIdx = getChartIndexForBackendBar(hovered.exit_bar);
          const midChartIdx = (entryChartIdx + exitChartIdx) / 2;
          const xVal =
            u.scales.x.distr === 2 ? midChartIdx : (u.data[0][Math.round(midChartIdx)] as number);
          const xPx = u.valToPos(xVal, 'x', true);
          const label = `Entry: $${entryPrice.toFixed(2)}  Exit: $${exitPrice.toFixed(2)}`;
          u.ctx.font = ANNOTATION_FONT;
          const metrics = u.ctx.measureText(label);
          const boxW = metrics.width + ANNOTATION_PADDING * 2;
          const boxH = 32;
          u.ctx.fillStyle = ANNOTATION_BG;
          u.ctx.fillRect(xPx - boxW / 2, top + 4, boxW, boxH);
          u.ctx.fillStyle = ANNOTATION_FG;
          u.ctx.textAlign = 'center';
          u.ctx.textBaseline = 'top';
          u.ctx.fillText(label, xPx, top + 10);
        }

        u.ctx.restore();
      },
    },
  };
}
